'use client';

import { useState, useMemo, useCallback, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import FlightSearchForm from './FlightSearchForm';
import ClaudeRecommendations from './ClaudeRecommendations';
import FlightResults from './FlightResults';
import FlightFilters from './FlightFilters';
import ClaudeChatWidget from '@/components/common/ClaudeChatWidget';
import { trackEvent } from '@/lib/analytics';

export interface SearchParams {
  tripType: 'one-way' | 'round-trip' | 'multi-city';
  from: string;
  to: string;
  departDate: string;
  returnDate?: string;
  passengers: {
    adults: number;
    children: number;
    infants: number;
  };
  cabinClass: 'economy' | 'premium-economy' | 'business' | 'first';
  multiCity?: Array<{
    from: string;
    to: string;
    date: string;
  }>;
}

const SearchContent = () => {
  const urlParams = useSearchParams();

  const defaultDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const defaultReturn = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const initialParams: SearchParams = useMemo(() => ({
    tripType: 'round-trip',
    from: urlParams.get('from') || 'London',
    to: urlParams.get('to') || 'Dubai',
    departDate: urlParams.get('date') || defaultDate,
    returnDate: urlParams.get('return') || defaultReturn,
    passengers: {
      adults: parseInt(urlParams.get('passengers') || '1'),
      children: 0,
      infants: 0,
    },
    cabinClass: 'economy',
  }), [urlParams, defaultDate, defaultReturn]);

  const [searchParams, setSearchParams] = useState<SearchParams>(initialParams);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    maxPrice: 5000,
    stops: 'any\' as \'any\' | \'0\' | \'1\' | \'2+',
    airlines: [] as string[],
    departTimeRange: 'any\' as \'any\' | \'morning\' | \'afternoon\' | \'evening',
  });

  // Auto-search if URL has from/to params (coming from homepage)
  useEffect(() => {
    const fromParam = urlParams.get('from');
    const toParam = urlParams.get('to');
    if (fromParam && toParam) {
      const params: SearchParams = {
        tripType: 'round-trip',
        from: fromParam,
        to: toParam,
        departDate: urlParams.get('date') || defaultDate,
        returnDate: urlParams.get('return') || defaultReturn,
        passengers: {
          adults: parseInt(urlParams.get('passengers') || '1'),
          children: 0,
          infants: 0,
        },
        cabinClass: 'economy',
      };
      setSearchParams(params);
      setHasSearched(true);
    }
  }, [urlParams, defaultDate, defaultReturn]);

  const handleSearch = useCallback((params: SearchParams) => {
    setSearchParams(params);
    setHasSearched(true);
    trackEvent('flight_search', {
      trip_type: params.tripType,
      from: params.from,
      to: params.to,
      cabin_class: params.cabinClass,
      total_passengers: params.passengers.adults + params.passengers.children + params.passengers.infants,
    });
  }, []);

  const chatContext = useMemo(() => ({
    page: 'flight-search',
    searchParams: hasSearched ? searchParams : undefined,
  }), [hasSearched, searchParams]);

  return (
    <div>
      {/* Search Form Section */}
      <div className="bg-gradient-to-br from-primary via-secondary to-ocean pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center py-10 lg:py-14">
            <h1 className="font-heading text-4xl lg:text-5xl font-bold text-white mb-4">
              Search Flights Worldwide
            </h1>
            <p className="text-lg text-white/90 mb-2">
              Compare prices from 500+ airlines — Book direct with us or via Skyscanner
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 rounded-full text-white text-xs font-bold">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                Live Prices Updated
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 rounded-full text-white text-xs font-bold">
                ✈️ 500+ Airlines
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 rounded-full text-white text-xs font-bold">
                💷 GBP Pricing
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 rounded-full text-white text-xs font-bold">
                📞 Expert Support 24/7
              </span>
            </div>
          </div>
          <FlightSearchForm onSearch={handleSearch} initialParams={searchParams} />
        </div>
      </div>

      {/* Results Section */}
      {hasSearched ? (
        <div className="bg-background">
          <div className="container mx-auto px-4 py-8">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Filters Sidebar */}
              <aside className="lg:w-72 flex-shrink-0">
                <FlightFilters
                  activeFilters={activeFilters}
                  onFiltersChange={setActiveFilters}
                  searchParams={searchParams}
                />
              </aside>
              {/* Results */}
              <div className="flex-1">
                <FlightResults searchParams={searchParams} activeFilters={activeFilters} />
              </div>
            </div>
          </div>
          <div className="container mx-auto px-4 pb-8">
            <ClaudeRecommendations searchParams={searchParams} />
          </div>
        </div>
      ) : (
        <div className="bg-background py-16">
          <div className="container mx-auto px-4">
            {/* Popular Routes */}
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-black text-foreground mb-6 text-center">Popular Routes</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  { from: 'London', to: 'Dubai', price: '£189', flag: '🇦🇪' },
                  { from: 'London', to: 'New York', price: '£299', flag: '🇺🇸' },
                  { from: 'Manchester', to: 'Ibiza', price: '£89', flag: '🇪🇸' },
                  { from: 'London', to: 'Bangkok', price: '£399', flag: '🇹🇭' },
                  { from: 'London', to: 'Maldives', price: '£549', flag: '🇲🇻' },
                  { from: 'Birmingham', to: 'Lahore', price: '£349', flag: '🇵🇰' },
                  { from: 'London', to: 'Karachi', price: '£379', flag: '🇵🇰' },
                  { from: 'London', to: 'Toronto', price: '£329', flag: '🇨🇦' },
                ].map((route) => (
                  <button
                    key={`${route.from}-${route.to}`}
                    onClick={() => {
                      const params: SearchParams = {
                        tripType: 'round-trip',
                        from: route.from,
                        to: route.to,
                        departDate: defaultDate,
                        returnDate: defaultReturn,
                        passengers: { adults: 1, children: 0, infants: 0 },
                        cabinClass: 'economy',
                      };
                      handleSearch(params);
                    }}
                    className="bg-card border-2 border-border hover:border-primary rounded-xl p-4 text-left transition-all hover:shadow-lg group"
                  >
                    <div className="text-2xl mb-2">{route.flag}</div>
                    <p className="text-xs text-muted-foreground font-bold">{route.from}</p>
                    <p className="text-sm font-black text-foreground">→ {route.to}</p>
                    <p className="text-primary font-black text-sm mt-1">from {route.price}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <ClaudeChatWidget context={chatContext} />
    </div>
  );
};

const SearchInteractive = () => {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gradient-to-br from-primary via-secondary to-ocean flex items-center justify-center">
        <div className="text-center text-white">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-bold">Loading flight search...</p>
        </div>
      </div>
    }>
      <SearchContent />
    </Suspense>
  );
};

export default SearchInteractive;
